/**
 * Where the fill happens, said beside every "Open the posting" (E4).
 *
 * JobCork fills a form in Chrome, with the JobCork extension, on the
 * posting's own page. A person who opens a posting from JobCork in another
 * browser -- Safari, on a Mac, because that is where the link opened --
 * sees the employer's plain form with nothing of JobCork on it, and has no
 * way to know why. Steven did exactly that on 6 Sept, on the first posting
 * he meant to apply to. This says which browser the fill needs, whether
 * this one has the extension, and whether the posting is on a site the
 * extension fills at all.
 */

import type { ExtensionStatus } from "./extension";

export type Browser = "chromium" | "other";

/** Chrome, or a browser built on it (Edge, Brave, Arc) -- all of them run the extension. */
export function browserFrom(ua: string, brands?: ReadonlyArray<{ brand: string }> | null): Browser {
  if (brands && brands.some((b) => /chromium|google chrome/i.test(b.brand))) return "chromium";
  if (/\b(Chrome|Chromium|CriOS)\//.test(ua)) return "chromium";
  return "other";
}

export type Ats = "greenhouse" | "lever" | "ashby" | "workable" | "workday" | null;

/** The ATS a posting's apply page is on, from its host. Null: a site JobCork does not know. */
export function atsOf(applyUrl: string | null | undefined): Ats {
  if (!applyUrl) return null;
  let host: string;
  try { host = new URL(applyUrl).host.toLowerCase(); } catch { return null; }
  if (host === "boards.greenhouse.io" || host === "job-boards.greenhouse.io" || host.endsWith(".greenhouse.io")) return "greenhouse";
  if (host === "jobs.lever.co") return "lever";
  if (host === "jobs.ashbyhq.com") return "ashby";
  if (host === "apply.workable.com") return "workable";
  if (host.endsWith(".myworkdayjobs.com") || host.endsWith(".myworkdaysite.com")) return "workday";
  return null;
}

/**
 * How far the cloud can take a submission on this source unattended (E18).
 * Mirrors SEND_MODE in @jobcork/apply (rules.ts) — kept here because the
 * web bundle does not depend on that package. "auto" = no human check, the
 * cloud submits with the computer off; "assisted" = a bot check gates
 * submit, so a person finishes it (Finish in Chrome) or the extension fills
 * it in their own browser. Verified per ATS 7 Sept 2026: all four we fill
 * carry a check, so all are "assisted" until a check-free source is added.
 */
export type SendMode = "auto" | "assisted";

export function sendModeOf(applyUrl: string | null | undefined): SendMode | null {
  const ats = atsOf(applyUrl);
  if (ats === null || ats === "workday") return null;
  const byAts: Record<Exclude<Ats, "workday" | null>, SendMode> = {
    greenhouse: "assisted", lever: "assisted", ashby: "assisted", workable: "assisted",
  };
  return byAts[ats];
}

/**
 * The apply route for a posting, as one of the three kinds the seeker sees.
 * Derived from the apply URL's host -- always the real destination for an
 * ATS-fetched sighting -- because a freshly ingested sighting carries no
 * resolved apply_kind yet, while its host is known the moment it is seen.
 *
 *   "auto"     -- a fillable ATS with no bot check: the cloud fills AND submits
 *                 it unattended, computer off. None today -- every ATS JobCork
 *                 fills currently carries a check -- so this lights up only when
 *                 a check-free source is added.
 *   "assisted" -- a fillable ATS with a bot check: JobCork fills every field and
 *                 the person clears the check and presses Submit (Finish in
 *                 Chrome), or the extension fills it in their own browser.
 *   "manual"   -- Workday, an employer's own site, an aggregator, anything else:
 *                 JobCork does not fill it. The person applies on the link and
 *                 marks it applied here, so it is tracked all the same.
 *
 * The single source of truth for the three types: every tag and filter reads
 * it, so they can never disagree about which kind a posting is.
 */
export type ApplyType = "auto" | "assisted" | "manual";

export function applyType(applyUrl: string | null | undefined): ApplyType {
  const mode = sendModeOf(applyUrl);
  if (mode === null) return "manual";
  return mode === "auto" ? "auto" : "assisted";
}

export interface Hint {
  tone: "ok" | "muted" | "err";
  text: string;
  /** Show the connect page (#extension) beside it. */
  connect?: boolean;
}

/**
 * The one line for this posting in this browser. `ext` is what the app
 * learned from the extension on this page: null while it is still asking.
 */
export function fillHint(applyUrl: string | null | undefined, ext: ExtensionStatus | null, browser: Browser, autoMode: "auto" | "review" = "review"): Hint | null {
  const ats = atsOf(applyUrl);
  if (!applyUrl) return null;
  if (ats === "workday") return { tone: "muted", text: "JobCork does not fill Workday forms yet — apply on the employer's site and mark it applied here." };
  if (ats === null) return { tone: "muted", text: "JobCork fills forms on Greenhouse, Lever, Ashby and Workable. This posting is elsewhere — apply on the employer's site and mark it applied here." };
  if (browser === "other") {
    return { tone: "err", text: "This browser is not Chrome, so the posting opens as the employer's plain form. Press “Send with JobCork” instead: you approve what goes, and JobCork fills and submits it from the cloud. Or open this posting in Chrome, where the extension fills it for you to check." };
  }
  if (ext === null) return { tone: "muted", text: "Checking for the JobCork extension…" };
  if (!ext.installed) return { tone: "err", text: "The JobCork extension is not installed in this browser, so the posting opens as the employer's plain form. Press “Send with JobCork” to send it from the cloud, or install the extension and open this page again.", connect: true };
  if (!ext.connected) return { tone: "muted", text: "The JobCork extension is here but not connected yet — a moment, or open the connect page.", connect: true };
  return autoMode === "auto"
    ? { tone: "ok", text: "Auto sends this on its next pass if it clears your rules. Or open it: JobCork fills the form in the tab that opens, and you press Submit." }
    : { tone: "ok", text: "JobCork fills the form in the tab that opens — check it, then press the form's own Submit." };
}
